"use client";

/**
 * The listing page's tabs under the description (feedback ticket 65,
 * 2026-11-01): "Bu kompleksdə" and "Yaxınlıqda" arrive rendered by the
 * server (the SEO cards, so their links are in the HTML a crawler reads;
 * the tab that is not shown keeps its panel with `hidden`), and
 * "Görünüşcə oxşar" is fetched once the block is about to be seen
 * (SimilarByLook). The page opens on the first tab with listings, which
 * the server already knows, so nothing moves while the page loads: the
 * lookalikes only add a tab at the end of the bar.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import AroundTabBar, { activeTab, type AroundTab, type AroundTabItem } from "@/components/listings/AroundTabs";
import { LookalikeGrid, useLookalikes } from "@/components/listings/SimilarByLook";
import { trackEvent } from "@/lib/tracking";

const LOOKS_LIMIT = 6;

export default function ListingAroundTabs({
  listingId,
  active,
  labels,
  building,
  nearby,
}: {
  listingId: string;
  /** A listing that is gone gets no lookalikes (the photo index keeps
   *  live listings only). */
  active: boolean;
  labels: { title: string; building: string; nearby: string; looks: string };
  /** The residence tab's panel and its true count; null outside one. */
  building: { total: number; panel: ReactNode } | null;
  /** The nearby tab's panel; null when it has no listings. */
  nearby: ReactNode | null;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!active || !el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      setSeen(true);
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [active]);
  const looks = useLookalikes(active ? listingId : null, LOOKS_LIMIT, seen);

  const tabs: AroundTabItem[] = [
    ...(building ? [{ id: "building" as const, label: labels.building, count: building.total }] : []),
    ...(nearby ? [{ id: "nearby" as const, label: labels.nearby }] : []),
    ...(looks && looks.length ? [{ id: "looks" as const, label: labels.looks }] : []),
  ];
  const [picked, setPicked] = useState<AroundTab | null>(null);
  const tab = activeTab(picked, tabs);
  const idBase = "listing-around";

  // The anchor stays for the observer while there is nothing to show yet.
  if (!tab) return <section ref={ref} aria-hidden />;

  const panel = (id: AroundTab, children: ReactNode) => (
    <div
      key={id}
      role="tabpanel"
      id={`${idBase}-panel-${id}`}
      aria-labelledby={`${idBase}-tab-${id}`}
      hidden={tab !== id}
    >
      {children}
    </div>
  );

  return (
    <section ref={ref} className="space-y-3">
      <h2 className="text-lg font-bold tracking-tight text-gray-900">{labels.title}</h2>
      <AroundTabBar
        tabs={tabs}
        value={tab}
        onChange={(next) => {
          setPicked(next);
          trackEvent("listing.around.tab", { id: listingId, tab: next, in: "page" });
        }}
        idBase={idBase}
        label={labels.title}
      />
      {building && panel("building", building.panel)}
      {nearby && panel("nearby", nearby)}
      {looks && looks.length > 0 && panel("looks", <LookalikeGrid items={looks} />)}
    </section>
  );
}
